import openCompose from './openCompose';
import trySaveAndCloseCompose from './trySaveAndCloseCompose';
import {
    ComposeViewState,
    ComposeOperation,
    ComposeTarget,
    getStore,
} from 'owa-mail-compose-store';
import filterNdrSubmittedRecipients from '../utils/filterNdrSubmittedRecipients';
import { HTML_BODY_TYPE } from '../utils/getDefaultBodyType';
import tryGetSmimeViewState from '../utils/tryGetSmimeViewState';
import { AppendOnSend } from 'owa-addins-core';
import isEncryptionEnabled from 'owa-encryption-common/lib/utils/isEncryptionEnabled';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getExtendedProperty } from 'owa-mail-store/lib/utils/getExtendedPropertyValue';
import { ClientItem, mailStore } from 'owa-mail-store';
import getItem from 'owa-mail-store/lib/services/getItem';
import checkIfResponseSuccess from 'owa-mail-store/lib/services/utils/checkIfResponseSuccess';
import { getComposeItemResponseShape } from 'owa-mail-store/lib/utils/getItemResponseShape';
import isMeetingRequest from 'owa-meeting-message/lib/utils/isMeetingRequest';
import { isDeepLink } from 'owa-popout';
import BodyType from 'owa-service/lib/contract/BodyType';
import ItemInfoResponseMessage from 'owa-service/lib/contract/ItemInfoResponseMessage';
import Message from 'owa-service/lib/contract/Message';
import SingleResponseMessage from 'owa-service/lib/contract/SingleResponseMessage';
import { lazySetSmimePropertiesInDraftMessage } from 'owa-smime';
import SmimeType from 'owa-smime-adapter/lib/store/schema/SmimeType';
import doesItemNeedToBeFetchedAgain from 'owa-smime/lib/utils/doesItemNeedToBeFetchedAgain';
import getStampedHeaderForSmime from 'owa-smime/lib/utils/getStampedHeaderForSmime';
import isSMIMEItem from 'owa-smime/lib/utils/isSMIMEItem';
import { isSxSDisplayed } from 'owa-sxs-store';
import { getStore as getTabStore, lazyActivateTab, TabType, primaryTab } from 'owa-tab-store';
import { trace } from 'owa-trace';
import { action } from 'satcheljs/lib/legacy';
import isCLPEnabled from 'owa-mail-clp/lib/utils/isCLPEnabled';
import getCLPLabelExtendedPropertyValue from 'owa-mail-clp/lib/utils/getCLPLabelExtendedPropertyValue';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import {
    getFolderOwnerEmailAddress,
    getFolderPermission,
    getIsInSharedFolder,
} from '../utils/sharedFolderUtils';

/**
 * This function gets the draft message response from server.
 * For S/MIME clear/opaque signed mail we need to stamp X-OWA-SmimeInstalled header
 * in request api, in order to get the p7m file as attachment in draft message response
 * so that we can decode it and load it to compose.
 */
async function getItemResponse(itemId: string): Promise<SingleResponseMessage[]> {
    const cachedItem = mailStore.items.get(itemId);
    /**
     * If item is already present in mailStore, we can determine whether
     * it is an S/MIME item and thus get the appropriate S/MIME headers
     * for it.
     */
    const headers = await getStampedHeaderForSmime(cachedItem);
    // Pass null as body type to use original body type of the draft
    const shape = getComposeItemResponseShape(null /*bodyType*/);
    const response = await getItem([itemId], shape, 'MailCompose', null, { headers });
    if (isFeatureEnabled('cmp-smime') && checkIfResponseSuccess(response[0])) {
        const responseMessage = response[0] as ItemInfoResponseMessage;
        const message = responseMessage.Items[0] as Message;

        /**
         * Item might need to be fetched again to get the p7m dataPackage in case this was a clear/opaque signed message,
         * but ItemClass was not known before the first fetch.
         * eg: in case of popout and deeplink
         */
        if (doesItemNeedToBeFetchedAgain(message as ClientItem)) {
            const headers = await getStampedHeaderForSmime(message);
            return getItem([itemId], shape, 'MailCompose', null /* requestServerVersion */, {
                headers,
            });
        }
    }

    return response;
}

/**
 * This function merges the decoded S/MIME properties
 * to draftMessage and returns smimeType of the decoded
 * message which is used to initialize composeViewState
 *
 * @param message S/MIME message to decode.
 * @return SmimeType of the decoded message.
 */
function processSmimeProperties(message: Message): Promise<SmimeType> {
    return lazySetSmimePropertiesInDraftMessage
        .import()
        .then(setSmimePropertiesInDraftMessage => setSmimePropertiesInDraftMessage(message));
}

export default action('loadDraftToCompose')(async function loadDraftToCompose(
    itemId: string,
    openTarget: ComposeTarget = ComposeTarget.SecondaryTab,
    isNdrResend?: boolean
) {
    // 1. Adjust open target
    if (openTarget == ComposeTarget.Popout) {
        // no op, always respect the setting
    } else if (isSxSDisplayed()) {
        // When SxS is opened, always open in SxS and override the setting
        openTarget = ComposeTarget.SxS;
    } else if (!isDeepLink() && openTarget === ComposeTarget.SecondaryTab) {
        // no op, tab view is available, so respect the setting
    } else {
        openTarget = ComposeTarget.PrimaryReadingPane;
    }

    // 2. try to reuse existing compose tab if any
    if (
        openTarget == ComposeTarget.PrimaryReadingPane ||
        openTarget === ComposeTarget.SecondaryTab
    ) {
        const tab = findProperTabByDraftItemId(itemId);
        if (tab) {
            // If there is a tab for this draft, activate it
            await lazyActivateTab.importAndExecute(tab);
            return;
        } else if (openTarget == ComposeTarget.PrimaryReadingPane) {
            // Otherwise, save current draft and close it in prmary tab if any when open target is primary
            await trySaveAndCloseCompose(null /*viewState*/);

            // If compose is still available in primary tab, it means it failed to save, so leave it there
            if (getStore().primaryComposeId) {
                return;
            }
        }
    }

    // 3. Start load draft
    try {
        const response = await getItemResponse(itemId);
        if (checkIfResponseSuccess(response[0])) {
            const responseMessage = response[0] as ItemInfoResponseMessage;
            let message = responseMessage.Items[0] as Message;
            let smimeType: SmimeType = SmimeType.None;
            if (isFeatureEnabled('cmp-smime') && isSMIMEItem(message)) {
                smimeType = await processSmimeProperties(message);
            }
            const bodyType: BodyType = message.Body.BodyType == HTML_BODY_TYPE ? 'HTML' : 'Text';
            const appendOnSendExtendedProperty = getExtendedProperty(
                message,
                null /*tag*/,
                'AppendOnSend'
            );
            let appendOnSend: AppendOnSend[];
            if (appendOnSendExtendedProperty) {
                appendOnSend = JSON.parse(appendOnSendExtendedProperty.Value);
            }
            appendOnSend = !!appendOnSend ? appendOnSend : []; // if no value, initialize to empty list
            const isInlineCompose = false;
            const isInSharedFolder = getIsInSharedFolder(message.ItemId);
            const conversationId = !!message.ConversationId ? message.ConversationId.Id : undefined;
            let existingLabelString = '';
            if (isCLPEnabled()) {
                existingLabelString = getCLPLabelExtendedPropertyValue(message);
            }
            // Populate rootItemId for each attachment since it's not populated in getItem response
            message.Attachments &&
                message.Attachments.forEach(attachment => {
                    attachment.AttachmentId.RootItemId = itemId;
                });

            const composeInitProps = {
                appendOnSend: appendOnSend,
                operation: ComposeOperation.EditDraft,
                bodyType: bodyType,
                itemId: message.ItemId,
                subject: message.Subject,
                quotedBody: message.Body.Value,
                to:
                    isNdrResend && message.ToRecipients
                        ? filterNdrSubmittedRecipients(message.ToRecipients)
                        : message.ToRecipients,
                cc: message.CcRecipients,
                bcc: message.BccRecipients,
                attachments: message.Attachments,
                importance: message.Importance,
                isReadReceiptRequested: message.IsReadReceiptRequested,
                isDeliveryReceiptRequested: message.IsDeliveryReceiptRequested,
                preferAsyncSend: true,
                from: message.From != null ? message.From.Mailbox : null,
                pendingSocialActivityTags: message.PendingSocialActivityTagIds,
                IRMData: isEncryptionEnabled() ? message.RightsManagementLicenseData : null,
                meetingRequestItem:
                    isMeetingRequest(message.ItemClass) != null ? { ...message } : null,
                sensitivity: message.Sensitivity,
                isInlineCompose: isInlineCompose,
                conversationId: conversationId,
                smimeViewState: await tryGetSmimeViewState(
                    ComposeOperation.EditDraft,
                    message,
                    smimeType
                ),
                isInSharedFolder: isInSharedFolder,
                existingLabelString: existingLabelString,
                folderOwnerEmailAddress: isInSharedFolder
                    ? getFolderOwnerEmailAddress(message.ItemId)
                    : undefined,
                folderPermission: isInSharedFolder
                    ? getFolderPermission(message.ItemId)
                    : undefined,
            };

            await openCompose(composeInitProps, openTarget, null);
        }
    } catch (error) {
        // TODO: handle error. Add S/MIME Draft infobars
        trace.info(`[loadDraftToCompose] ${error.message}`);
    }
});

/**
 * If we have already opened the draft item in a tab, either in inline compose or full compose,
 * we should not open a new compose for it, but just reuse the existing one
 */
function findProperTabByDraftItemId(draftItemId: string) {
    const composeStore = getStore();
    let composeViewState: ComposeViewState;
    composeStore.viewStates.forEach(vs => {
        if (vs.itemId && vs.itemId.Id == draftItemId) {
            composeViewState = vs;
        }
    });

    if (composeViewState) {
        const tabs = getTabStore().tabs;
        const composeId = composeViewState.composeId;

        if (composeId == composeStore.primaryComposeId) {
            // First, check if compose is already opened in primary tab
            return primaryTab;
        } else if (composeViewState.isInlineCompose) {
            // Second, try to find reading pane which has inline compose opened with this draft item
            return tabs.filter(
                tab =>
                    tab.type == TabType.SecondaryReadingPane &&
                    tab.data.listViewType == ReactListViewType.Conversation &&
                    tab.data.id &&
                    tab.data.id.Id == composeViewState.conversationId
            )[0];
        } else {
            // Third, try to find full compose tab with this draft item id
            return tabs.filter(tab => tab.type == TabType.Compose && tab.data == composeId)[0];
        }
    }

    // Last, null of the above case, returns null
    return null;
}
